import type { FC, ReactNode } from 'react'

import Image from 'next/image'
import Link from 'next/link'
import { ArrowRight, Check } from 'lucide-react'
import { SectionHeading } from '@/components/generals/section-heading'
import { TreeRings } from '@/components/generals/tree-rings'
import { buttonVariants } from '@/components/ui/button'
import { CUSTOM_POINTS } from '@/constants/content'

export const CustomBuildSection: FC = (): ReactNode => {
    return (
        <section id='custom-builds' className='overflow-hidden py-20 lg:py-28'>
            <div className='mx-auto grid max-w-7xl items-center gap-14 px-5 sm:px-8 lg:grid-cols-12 lg:gap-16'>
                <div className='lg:col-span-6'>
                    <SectionHeading
                        eyebrow="Custom builds"
                        title="Have something else in mind?"
                        description="A mobile office, a holiday rental, a garden studio or a home designed around a wheelchair. If none of our models fit, we design one that does."
                    />

                    <ul className='mt-8 space-y-4'>
                        {CUSTOM_POINTS.map(point => (
                            <li key={point} className='flex items-start gap-3'>
                                <span className='mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground'>
                                    <Check className='size-3.5' aria-hidden='true' />
                                </span>

                                <span className='leading-relaxed'>
                                    {point}
                                </span>
                            </li>
                        ))}
                    </ul>

                    <div className='mt-10 flex flex-wrap gap-3'>
                        <Link href='/contact?model=custom' className={buttonVariants({ variant: 'timber', size: 'lg' })}>
                            Start a custom project
                            <ArrowRight className='size-4' />
                        </Link>

                        <Link href='/process#options' className={buttonVariants({ variant: 'ghost', size: 'lg' })}>
                            See options and upgrades
                        </Link>
                    </div>
                </div>

                <div className='relative lg:col-span-6'>
                    <TreeRings className='pointer-events-none absolute -top-16 -right-24 size-[30rem] text-timber/20' />

                    <div className='relative ml-auto aspect-[4/3] overflow-hidden rounded-[2rem] bg-secondary sm:aspect-[5/4] lg:aspect-[4/5] lg:max-w-md'>
                        <Image
                            src='/images/craft.jpg'
                            alt="Hand tools with wooden handles laid out on a pale timber board"
                            fill
                            sizes='(min-width: 1024px) 28rem, 100vw'
                            className='object-cover'
                        />
                    </div>
                </div>
            </div>
        </section>
    )
}
